import { check } from './check';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ValidationResult,
  ValidationResultBasic,
  ValidationRule,
} from './types';
import { type Language } from '@funnellab/shared/src/types/directus-enums';
import { useDeepCompareEffect } from 'rooks';
import equal from 'fast-deep-equal';

export type ValidationResultPosition = 'message' | 'tooltip' | 'none';

export function useValidation<T>(
  value: T | null | undefined,
  rules: ValidationRule<T>[],
  onCheckPassed: ((val: typeof value) => void) | undefined,
  validationMsgLng?: Language,
): ValidationResult {
  const initialized = useRef(false);
  const [isValid, setIsValid] = useState(true);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [errorMessageParams, setErrorMessageParams] = useState<
    (object | undefined)[]
  >([]);
  const [resultsPerItem, setResultsPerItem] = useState<ValidationResultBasic[]>(
    [],
  );

  // Store last computed result to avoid setting identical state over and over
  const lastResultRef = useRef<Required<ValidationResult> | null>(null);

  const { t } = useTranslation('formValidation', {
    lng: validationMsgLng,
  });

  useDeepCompareEffect(() => {
    const checkResult = check(t, value, rules);
    if (initialized.current && checkResult.isValid && onCheckPassed) {
      onCheckPassed(value);
    }

    const prev = lastResultRef.current;
    if (!prev || !equal(checkResult, prev)) {
      setIsValid(checkResult.isValid);
      setErrorMessages(checkResult.errorMessages);
      setErrorMessageParams(checkResult.errorMessageParams);
      setResultsPerItem(checkResult.resultsPerItem);
      lastResultRef.current = checkResult;
    }
    initialized.current = true;
  }, [value, rules]);

  return {
    isValid,
    errorMessages,
    errorMessageParams,
    resultsPerItem,
  };
}
